import React, { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Button,
  Input,
  InputAdornment,
  Paper,
  Toolbar,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';

import { AppDataGrid, Page, toast } from 'components';
import { useMisc } from 'store';
import { callApi } from 'utils';

const parsePrice = value => (value === '' ? null : Number(value));

const PriceSourceCell = ({row, api}) => {
  if (!row.overridden) return '渠道默认';

  const resetPrice = async (ev) => {
    ev.stopPropagation();
    try {
      const next = await callApi(`/order-prices/${row.parent}`, 'DELETE');
      api.updateRows([next]);
      toast('已恢复渠道默认价格');
    } catch {
      toast('恢复渠道默认价格失败', 'error');
    }
  };

  return (
    <Button size="small" onClick={resetPrice}>
      恢复渠道默认
    </Button>
  );
};

const OrderPrices = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const misc = useMisc((state) => state.data);

  const dataUrl = useMemo(() => {
    const params = new URLSearchParams();
    const q = searchParams.get('q');
    q && params.set('q', q);
    const suffix = params.toString();
    return `/order-prices${suffix ? `?${suffix}` : ''}`;
  }, [searchParams]);

  const columns = useMemo(() => [
    {
      field: 'parent',
      headerName: '订单ID',
      minWidth: 100,
      flex: 0.7,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'name',
      headerName: '订单名称',
      minWidth: 320,
      flex: 3,
    },
    {
      field: 'org',
      headerName: '渠道',
      minWidth: 130,
      flex: 1,
      renderCell: params => misc.org?.[params.value] || params.value,
    },
    {
      field: 'imp_price',
      headerName: '展示 CPM 单价',
      type: 'number',
      minWidth: 150,
      flex: 1,
      editable: true,
      valueParser: parsePrice,
    },
    {
      field: 'click_price',
      headerName: '点击单价',
      type: 'number',
      minWidth: 150,
      flex: 1,
      editable: true,
      valueParser: parsePrice,
    },
    {
      field: 'price_source',
      headerName: '价格来源',
      minWidth: 160,
      flex: 1,
      sortable: false,
      renderCell: params => <PriceSourceCell {...params} />,
    },
  ], [misc.org]);

  const submitSearch = useCallback((ev) => {
    ev.preventDefault();
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      const q = query.trim();
      q ? next.set('q', q) : next.delete('q');
      next.delete('page');
      return next;
    });
  }, [query, setSearchParams]);

  const processRowUpdate = useCallback(async (next, old) => {
    if (
      next.imp_price === old.imp_price
      && next.click_price === old.click_price
    ) return old;

    const row = await callApi(`/order-prices/${next.parent}`, 'PUT', {
      imp_price: next.imp_price,
      click_price: next.click_price,
    });
    toast('价格已更新');
    return row;
  }, []);

  const handleUpdateError = useCallback(() => {
    toast('价格更新失败', 'error');
  }, []);

  return (
    <Page title="订单价格" styles={{padding: 3}}>
      <Paper sx={{p: 1, pb: 0}}>
        <Toolbar component="form" onSubmit={submitSearch} sx={{px: 0}}>
          <Input
            name="q"
            value={query}
            placeholder="订单ID或名称"
            onChange={ev => setQuery(ev.target.value)}
            startAdornment={
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            }
            sx={{width: 260}}
          />
        </Toolbar>
        <AppDataGrid
          columns={columns}
          dataUrl={dataUrl}
          idColumn="parent"
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={handleUpdateError}
        />
      </Paper>
    </Page>
  );
};

export default OrderPrices;
